/**
 * Zustand store for the P2P chat feature — multi-session collection.
 *
 * Holds N concurrent {@link ChatSession} metadata records plus the id of the
 * single foreground session. Stores ONLY non-sensitive UI/session metadata and
 * plaintext message lists:
 *   - the room code (already public to the two participants)
 *   - peer identity (roster id/name when known)
 *   - whether this peer is the initiator
 *   - whether relay-only mode is requested
 *   - connection status, unread count, pin flag, plaintext messages
 *
 * Cryptographic keys and peer connections live elsewhere (the `chat.ts` closure
 * owned by the module-level `sessionManager`) and are NEVER persisted here.
 *
 * The store is pure state: it never starts/stops WebRTC sessions. The
 * `sessionManager` (manager → store, one-way) writes connection updates in;
 * the store NEVER imports the manager (avoids a circular dependency).
 *
 * @module features/p2p-chat/stores/p2pChatStore
 */

import { create } from 'zustand';
import type { ChatMessage, ConnectionStatus } from '../types/p2pChat.types';

/** Maximum number of concurrently active chats (BA decision). */
export const MAX_SESSIONS = 5;
/** Maximum number of pinned chats (BA decision). */
export const MAX_PINNED = 3;

/**
 * A single active chat session's metadata + message log. Crypto keys never
 * appear here — they remain in the `chat.ts` closure.
 */
export interface ChatSession {
  /** Stable local id (also the sessionManager key). */
  readonly id: string;
  readonly roomCode: string;
  /** Roster id of the peer when known (invite/favorite); else null. */
  readonly peerId: string | null;
  /** Display name of the peer when known; else null. */
  readonly peerName: string | null;
  readonly initiator: boolean;
  readonly relayOnly: boolean;
  readonly status: ConnectionStatus;
  readonly messages: readonly ChatMessage[];
  readonly unreadCount: number;
  readonly pinned: boolean;
  readonly error: string | null;
  /** Created via `Date.now()`; used for recency ordering. */
  readonly createdAt: number;
}

/** Metadata required to register a new session in the store. */
export interface NewSessionMeta {
  readonly roomCode: string;
  readonly peerId: string | null;
  readonly peerName: string | null;
  readonly initiator: boolean;
  readonly relayOnly: boolean;
}

interface P2PChatState {
  readonly sessions: readonly ChatSession[];
  readonly foregroundId: string | null;
  /** Global relay-only preference applied to newly created sessions. */
  readonly relayOnly: boolean;

  readonly setRelayOnly: (value: boolean) => void;
  /** Register a new session. Returns its generated id. */
  readonly addSession: (meta: NewSessionMeta) => string;
  /** Remove a session and re-select the foreground (next session or null). */
  readonly closeSession: (id: string) => void;
  /** Bring a session to the foreground (also marks it read). */
  readonly setForeground: (id: string | null) => void;
  /** Reset a session's unread count to zero. */
  readonly markRead: (id: string) => void;
  /** Increment a session's unread count by one. */
  readonly incrementUnread: (id: string) => void;
  readonly appendMessage: (id: string, message: ChatMessage) => void;
  readonly setStatus: (id: string, status: ConnectionStatus) => void;
  readonly setError: (id: string, error: string | null) => void;
  /** Toggle pin. Returns false (no-op) when pinning would exceed MAX_PINNED. */
  readonly togglePin: (id: string) => boolean;
  /**
   * Find an existing session by participants. Keyed on peerId when provided,
   * otherwise on roomCode. Used by the controller for dedupe.
   */
  readonly findByParticipants: (
    peerId: string | null,
    roomCode: string
  ) => ChatSession | undefined;
  /** True when a new session can be added without exceeding the cap. */
  readonly canAddSession: () => boolean;
  /** Remove all sessions (logout). Does NOT close manager sessions. */
  readonly clearAll: () => void;
}

function reselectForeground(
  sessions: readonly ChatSession[],
  removedId: string,
  currentForeground: string | null
): string | null {
  if (currentForeground !== removedId) return currentForeground;
  // Pick the most recently created remaining session.
  const remaining = sessions.filter((s) => s.id !== removedId);
  if (remaining.length === 0) return null;
  return (
    [...remaining].sort((a, b) => b.createdAt - a.createdAt)[0]?.id ?? null
  );
}

export const useP2PChatStore = create<P2PChatState>((set, get) => ({
  sessions: [],
  foregroundId: null,
  relayOnly: false,

  setRelayOnly: (value) => set({ relayOnly: value }),

  addSession: (meta) => {
    const id = crypto.randomUUID();
    const session: ChatSession = {
      id,
      roomCode: meta.roomCode,
      peerId: meta.peerId,
      peerName: meta.peerName,
      initiator: meta.initiator,
      relayOnly: meta.relayOnly,
      status: 'connecting',
      messages: [],
      unreadCount: 0,
      pinned: false,
      error: null,
      createdAt: Date.now(),
    };
    set((state) => ({ sessions: [...state.sessions, session] }));
    return id;
  },

  closeSession: (id) =>
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== id),
      foregroundId: reselectForeground(state.sessions, id, state.foregroundId),
    })),

  setForeground: (id) =>
    set((state) => ({
      foregroundId: id,
      sessions:
        id === null
          ? state.sessions
          : state.sessions.map((s) =>
              s.id === id ? { ...s, unreadCount: 0 } : s
            ),
    })),

  markRead: (id) =>
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === id ? { ...s, unreadCount: 0 } : s
      ),
    })),

  incrementUnread: (id) =>
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === id ? { ...s, unreadCount: s.unreadCount + 1 } : s
      ),
    })),

  appendMessage: (id, message) =>
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === id ? { ...s, messages: [...s.messages, message] } : s
      ),
    })),

  setStatus: (id, status) =>
    set((state) => ({
      sessions: state.sessions.map((s) => (s.id === id ? { ...s, status } : s)),
    })),

  setError: (id, error) =>
    set((state) => ({
      sessions: state.sessions.map((s) => (s.id === id ? { ...s, error } : s)),
    })),

  togglePin: (id) => {
    const session = get().sessions.find((s) => s.id === id);
    if (!session) return false;
    if (!session.pinned) {
      const pinnedCount = get().sessions.filter((s) => s.pinned).length;
      if (pinnedCount >= MAX_PINNED) return false;
    }
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === id ? { ...s, pinned: !s.pinned } : s
      ),
    }));
    return true;
  },

  findByParticipants: (peerId, roomCode) =>
    get().sessions.find((s) =>
      peerId !== null ? s.peerId === peerId : s.roomCode === roomCode
    ),

  canAddSession: () => get().sessions.length < MAX_SESSIONS,

  clearAll: () => set({ sessions: [], foregroundId: null }),
}));

// ============================================
// Selectors
// ============================================

/**
 * Order sessions for the switcher:
 *   1. pinned before unpinned (FR5/FR6 — pins take precedence),
 *   2. within each group, sessions with unread messages first,
 *   3. then by `createdAt` recency (newest first).
 *
 * Returns a new array — callers should wrap with `useShallow` to avoid
 * needless re-renders.
 */
export function selectOrderedSessions(
  sessions: readonly ChatSession[]
): readonly ChatSession[] {
  const rank = (s: ChatSession): number => {
    const pinnedBit = s.pinned ? 0 : 1;
    const unreadBit = s.unreadCount > 0 ? 0 : 1;
    return pinnedBit * 2 + unreadBit;
  };
  return [...sessions].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return b.createdAt - a.createdAt;
  });
}
